import { Typography } from '@forethought-technologies/forethought-elements';
import { Box, IconButton, useTheme } from '@mui/material';
import { IconX } from '@tabler/icons-react';
import { navigateTo, openWidget, setNavigateBackAction } from 'actions/main';
import { recordKnowledgeClick } from 'actions/workflows-v2/workflowsV2';
import { setSelectedArticle } from 'actions/workflows/workflows';
import { KnowledgeResult, KnowledgeSearchResponse } from 'api/types';
import { ARTICLE_PAGE } from 'constants/pageConsts';
import { useAppDispatch, useAppSelector } from 'hooks';
import React from 'react';
import {
  selectFreeFormQuery,
  selectFloatingBarSearchResult,
  setFloatingBarSearchResult,
} from 'reducers/workflows';
import { htmlDecode } from 'utils/htmlDecode';
import { postIframeResizeEvent } from 'utils/postIframeResizeEvent';
import { useWidgetArticleSearch } from './App/hooks/useWidgetArticleSearch';

export const FloatingArticleSuggestions = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const freeFormQuery = useAppSelector(selectFreeFormQuery);
  const floatingBarSearchResult = useAppSelector(selectFloatingBarSearchResult);

  // used to trigger the search when the freeFormQuery changes
  useWidgetArticleSearch({
    origin: 'floating_bar',
    query: freeFormQuery,
  });

  const onResultClick = ({
    document,
    fullResult,
  }: {
    document: KnowledgeResult;
    fullResult: KnowledgeSearchResponse;
  }) => {
    dispatch(setNavigateBackAction('close'));
    dispatch(
      setSelectedArticle({
        ...document,
        link: document.link || '',
      }),
    );
    dispatch(navigateTo(ARTICLE_PAGE));
    dispatch(openWidget());
    dispatch(recordKnowledgeClick({ document, fullResult }));
  };

  React.useEffect(() => {
    postIframeResizeEvent();
  }, [floatingBarSearchResult]);

  if (!floatingBarSearchResult || !floatingBarSearchResult.results.length) {
    return null;
  }

  return (
    <Box
      className='forethought-scrollbar'
      sx={{
        backgroundColor: theme.palette.colors.white,
        border: `1px solid ${theme.palette.colors.grey[300]}`,
        borderRadius: '4px',
        boxShadow:
          '2px 5px 8px 0px rgba(3, 17, 38, 0.1071), 0px 0px 1px 0px rgba(0, 12, 32, 0.04)',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginY: '8px',
        maxHeight: '300px',
        overflowX: 'hidden',
        overflowY: 'auto',
        padding: 2,
      }}
    >
      {/* label */}
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          color={theme.palette.colors.grey[600]}
          component='div'
          variant='font12'
        >
          Top knowledge results
        </Typography>
        <IconButton
          aria-label='Clear search results'
          onClick={() => {
            dispatch(setFloatingBarSearchResult(undefined));
          }}
        >
          <IconX color={theme.palette.colors.grey[600]} size={12} />
        </IconButton>
      </Box>
      {/* results */}
      {floatingBarSearchResult.results.map((result, index) => (
        <Box
          key={index}
          onClick={() => {
            onResultClick({
              document: result,
              fullResult: floatingBarSearchResult,
            });
          }}
          sx={{ cursor: 'pointer', gap: '4px' }}
        >
          <Typography component='div' variant='font14'>
            {result.title}
          </Typography>
          <Box
            sx={{
              color: theme.palette.colors.grey[500],
              fontSize: '12px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <Typography color={theme.palette.colors.grey[500]} variant='font12'>
              {htmlDecode(result.preview)}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};
